import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/*
 * HV-CART-BEST v1.0.3 (2026-09-16) — "every valid code is accepted, the best one is applied,
 * and the shopper can switch."
 *
 * Shopify already resolves two conflicting codes by keeping the one that saves more (measured
 * 2026-09-16 on this store: FREEROLLER beat ALIVEGUY10 at 8 rollers, ALIVEGUY10 beat FREEROLLER
 * at 11), and reports the loser as `applicable: false`. The stock component treated that flag as
 * "invalid code", blanked the field and showed the not-valid error — so a shopper holding a real
 * second code was told it could not be used, with no way to pick it instead.
 *
 * What changes:
 *  - A code that comes back `applicable: false` is probed ALONE. If it is valid on its own, it is
 *    kept as an ALTERNATE (sessionStorage, per tab) and shown under the pills with the total it
 *    would produce and a "Use instead" button. Only a code that is invalid alone gets the error.
 *  - If the new code wins and an existing one flips to `applicable: false`, the old one becomes
 *    the alternate the same way, so the switch is always reversible.
 *  - Removing a pill re-offers the alternates to Shopify, so the next-best code takes over.
 *  - Only APPLICABLE codes are left on the cart, so checkout never sees a losing code.
 *
 * Everything else (refs, morphing, events) is the stock Horizon component, untouched.
 */

const HV_ALT_KEY = 'hv_cart_alt_codes';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountErrorRequest] - The unconfirmed request error element.
 * @property {HTMLElement} [cartDiscountStatus] - The pending status element.
 * @property {HTMLElement} [hvAlt] - HV-CART-BEST: container for alternate codes.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} Apply owns its pending UI independently of remove/switch requests. */
  #pendingApply = null;

  /** @type {number | null} cart total in cents after the last update this component made */
  #lastTotal = null;

  connectedCallback() {
    super.connectedCallback();
    this.#renderAlternates();
    // component-cart-items re-renders this whole section on discount:update / cart:update,
    // which wipes the alternates box; redraw after it has had its turn.
    document.addEventListener(ThemeEvents.discountUpdate, this.#rerenderSoon);
    document.addEventListener(ThemeEvents.cartUpdate, this.#rerenderSoon);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.discountUpdate, this.#rerenderSoon);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#rerenderSoon);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#renderAlternates();
  }

  #rerenderSoon = () => {
    requestAnimationFrame(() => this.#renderAlternates());
    setTimeout(() => this.#renderAlternates(), 700);
    setTimeout(() => this.#renderAlternates(), 1800);
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();
    this.#pendingApply = abortController;
    const submitButton = form.querySelector('button[type="submit"]');
    const buttonLabel = submitButton?.textContent;
    form.setAttribute('aria-busy', 'true');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.textContent = 'Applying…';
    }
    if (this.refs.cartDiscountStatus) {
      this.refs.cartDiscountStatus.textContent = 'Checking your discount…';
      this.refs.cartDiscountStatus.hidden = false;
    }

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      // HV-CART-BEST: re-entering a parked alternate is the same as "use instead".
      const parked = this.#getAlternates().find((a) => a.code === discountCodeValue);
      if (parked) {
        discountCode.value = '';
        await this.#switchTo(parked.code, abortController.signal);
        return;
      }

      this.#clearDiscountErrors();

      const before = await this.#currentCart(abortController.signal);
      const data = await this.#postDiscounts([...existingDiscounts, discountCodeValue], abortController.signal);

      const newEntry = this.#codeEntry(data, discountCodeValue);
      if (!newEntry) throw new Error('Discount eligibility was not returned');

      if (newEntry && newEntry.applicable === false) {
        // HV-CART-BEST: not applicable alongside the current codes. Is it valid on its own?
        const probe = await this.#postDiscounts([discountCodeValue], abortController.signal);
        const probeEntry = this.#codeEntry(probe, discountCodeValue);
        const validAlone = Boolean(probeEntry && probeEntry.applicable === true);

        // Put the winning set back either way (the probe replaced it server-side).
        const restored = await this.#postDiscounts(existingDiscounts, abortController.signal);

        // The DOM already shows the restored state, so like the stock component: no morph here.
        discountCode.value = '';
        if (validAlone) {
          this.#addAlternate({ code: discountCodeValue, total: probe.total_price });
          this.#renderAlternates(restored.total_price);
          return;
        }
        this.#handleDiscountError('discount_code');
        return;
      }

      // HV-CART-BEST: the new code won — anything it displaced becomes an alternate.
      const displaced = existingDiscounts.filter((code) => {
        const entry = this.#codeEntry(data, code);
        return entry && entry.applicable === false;
      });
      for (const code of displaced) {
        this.#addAlternate({ code, total: before ? before.total_price : data.total_price });
      }

      let finalData = data;
      if (displaced.length > 0) {
        // Leave only applicable codes on the cart so checkout never sees a losing code.
        const winners = this.#applicableCodes(data);
        finalData = await this.#postDiscounts(winners, abortController.signal);
      }

      const newHtml = this.#sectionHtml(finalData);
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          displaced.length === 0 &&
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          finalData.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      discountCode.value = '';
      document.dispatchEvent(new DiscountUpdateEvent(finalData, this.id));
      await morphSection(this.dataset.sectionId, newHtml);
      this.#renderAlternates(finalData.total_price);
    } catch (error) {
      if (!abortController.signal.aborted) this.#handleDiscountError('request');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      if (this.#pendingApply === abortController) {
        this.#pendingApply = null;
        form.removeAttribute('aria-busy');
        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = false;
          submitButton.textContent = buttonLabel ?? 'Apply';
        }
        if (this.refs.cartDiscountStatus) this.refs.cartDiscountStatus.hidden = true;
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      this.#clearDiscountErrors();
      // HV-CART-BEST: re-offer the parked alternates so the next-best code takes over.
      const alternates = this.#getAlternates().map((a) => a.code);
      const candidates = [...existingDiscounts, ...alternates.filter((c) => !existingDiscounts.includes(c))];

      let data = await this.#postDiscounts(candidates, abortController.signal);

      if (alternates.length > 0) {
        const winners = this.#applicableCodes(data);
        const stillParked = this.#getAlternates().filter((a) => !winners.includes(a.code));
        this.#setAlternates(stillParked);
        if (winners.length !== candidates.length) {
          data = await this.#postDiscounts(winners, abortController.signal);
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, this.#sectionHtml(data));
      this.#renderAlternates(data.total_price);
    } catch (error) {
      if (!abortController.signal.aborted) this.#handleDiscountError('request');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  /**
   * HV-CART-BEST: apply a parked alternate instead of the current code(s).
   * @param {MouseEvent | KeyboardEvent} event
   */
  useAlternate = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!(event.target instanceof HTMLElement)) return;
    const button = event.target.closest('[data-hv-code]');
    if (!(button instanceof HTMLElement) || !button.dataset.hvCode) return;
    const abortController = this.#createAbortController();
    try {
      this.#clearDiscountErrors();
      await this.#switchTo(button.dataset.hvCode, abortController.signal);
    } catch (error) {
      if (!abortController.signal.aborted) this.#handleDiscountError('request');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  /**
   * HV-CART-BEST: forget a parked alternate.
   * @param {MouseEvent | KeyboardEvent} event
   */
  dismissAlternate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!(event.target instanceof HTMLElement)) return;
    const button = event.target.closest('[data-hv-code]');
    if (!(button instanceof HTMLElement) || !button.dataset.hvCode) return;
    const code = button.dataset.hvCode;
    this.#setAlternates(this.#getAlternates().filter((a) => a.code !== code));
    this.#renderAlternates();
  };

  /**
   * HV-CART-BEST: make `code` the applied code; whatever it displaces is parked.
   * @param {string} code
   * @param {AbortSignal} signal
   */
  async #switchTo(code, signal) {
    if (typeof this.dataset.sectionId !== 'string') return;
    const current = this.#existingDiscounts();
    const before = await this.#currentCart(signal);
    const data = await this.#postDiscounts([code], signal);
    const entry = this.#codeEntry(data, code);
    if (!entry || entry.applicable !== true) {
      // It stopped being valid (used up, expired, cart changed). Restore and drop it.
      const restored = await this.#postDiscounts(current, signal);
      this.#setAlternates(this.#getAlternates().filter((a) => a.code !== code));
      document.dispatchEvent(new DiscountUpdateEvent(restored, this.id));
      await morphSection(this.dataset.sectionId, this.#sectionHtml(restored));
      this.#renderAlternates(restored.total_price);
      this.#handleDiscountError('discount_code');
      return;
    }
    const parked = this.#getAlternates().filter((a) => a.code !== code);
    for (const old of current) {
      if (old !== code && !parked.some((a) => a.code === old)) {
        parked.push({ code: old, total: before ? before.total_price : data.total_price });
      }
    }
    this.#setAlternates(parked);
    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    await morphSection(this.dataset.sectionId, this.#sectionHtml(data));
    this.#renderAlternates(data.total_price);
  }

  /**
   * @param {string[]} codes
   * @param {AbortSignal} signal
   * @returns {Promise<any>}
   */
  async #postDiscounts(codes, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [this.dataset.sectionId],
        sections_url: window.location.pathname,
      }),
    });
    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });
    if (!response.ok) throw new Error('Discount update request failed');
    const data = await response.json();
    if (!Array.isArray(data?.discount_codes) || !Number.isFinite(data?.total_price)) {
      throw new Error('Discount update response was incomplete');
    }
    return data;
  }

  /** @param {any} data - Confirmed cart response with bundled section rendering. */
  #sectionHtml(data) {
    const html = data?.sections?.[this.dataset.sectionId];
    if (typeof html !== 'string' || !html.trim()) throw new Error('Cart section was not returned');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    if (!parsed.getElementById(`shopify-section-${this.dataset.sectionId}`)) {
      throw new Error('Cart section could not be rendered');
    }
    return html;
  }

  #clearDiscountErrors() {
    const refs = this.refs;
    for (const name of ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorRequest']) {
      refs[name]?.classList.add('hidden');
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<any | null>}
   */
  async #currentCart(signal) {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal,
      });
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {any} data
   * @param {string} code
   * @returns {{ code: string; applicable: boolean } | undefined}
   */
  #codeEntry(data, code) {
    const list = Array.isArray(data?.discount_codes) ? data.discount_codes : [];
    return list.find((/** @type {{ code: string; applicable: boolean }} */ d) => d.code.toLowerCase() === code.toLowerCase());
  }

  /**
   * @param {any} data
   * @returns {string[]}
   */
  #applicableCodes(data) {
    const list = Array.isArray(data?.discount_codes) ? data.discount_codes : [];
    return list
      .filter((/** @type {{ code: string; applicable: boolean }} */ d) => d.applicable === true)
      .map((/** @type {{ code: string }} */ d) => d.code);
  }

  /** @returns {{ code: string; total: number }[]} */
  #getAlternates() {
    try {
      const raw = window.sessionStorage.getItem(HV_ALT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((a) => a && typeof a.code === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  /** @param {{ code: string; total: number }[]} list */
  #setAlternates(list) {
    try {
      if (list.length === 0) window.sessionStorage.removeItem(HV_ALT_KEY);
      else window.sessionStorage.setItem(HV_ALT_KEY, JSON.stringify(list));
    } catch (error) {}
  }

  /** @param {{ code: string; total: number }} entry */
  #addAlternate(entry) {
    const list = this.#getAlternates().filter((a) => a.code !== entry.code);
    list.push(entry);
    this.#setAlternates(list);
  }

  /**
   * Read the rendered cart total (cents) as a fallback when this component has not updated yet.
   * @returns {number}
   */
  #domTotal() {
    const el = document.querySelector('.cart-totals__total-value');
    const text = el ? el.textContent || '' : '';
    const match = text.replace(/,/g, '').match(/(\d+)(?:\.(\d{1,2}))?/);
    if (!match) return NaN;
    return Number(match[1]) * 100 + Number((match[2] || '0').padEnd(2, '0'));
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * HV-CART-BEST: draw the parked alternates under the pills.
   * @param {number} [currentTotal] - cart total in cents after the last update, if known.
   */
  #renderAlternates(currentTotal) {
    const box = this.refs.hvAlt;
    if (!(box instanceof HTMLElement)) return;

    const winners = this.#existingDiscounts();
    const alternates = this.#getAlternates().filter((a) => !winners.includes(a.code));

    if (alternates.length === 0 || winners.length === 0) {
      box.hidden = true;
      box.replaceChildren();
      return;
    }

    if (typeof currentTotal === 'number') this.#lastTotal = currentTotal;
    const total = typeof this.#lastTotal === 'number' ? this.#lastTotal : this.#domTotal();

    const lead = document.createElement('p');
    lead.className = 'hv-alt__lead cart-primary-typography';
    const leadTemplate = box.dataset.hvLead || 'Best deal applied: {code} ({total})';
    lead.textContent = Number.isFinite(total)
      ? leadTemplate.replace('{code}', winners.join(' + ')).replace('{total}', this.#money(total))
      : leadTemplate.replace('{code}', winners.join(' + ')).replace(/\s*\({total}\)/, '').replace('{total}', '');

    const list = document.createElement('ul');
    list.className = 'hv-alt__list';

    for (const alt of alternates) {
      const item = document.createElement('li');
      item.className = 'hv-alt__item';

      const code = document.createElement('span');
      code.className = 'hv-alt__code';
      code.textContent = alt.code;

      const delta = document.createElement('span');
      delta.className = 'hv-alt__delta cart-secondary-typography';
      const template = box.dataset.hvWould || 'would make it {total}';
      delta.textContent = Number.isFinite(alt.total) ? template.replace('{total}', this.#money(alt.total)) : '';

      const use = document.createElement('button');
      use.type = 'button';
      use.className = 'hv-alt__use button-unstyled';
      use.dataset.hvCode = alt.code;
      use.textContent = box.dataset.hvUse || 'Use instead';
      use.addEventListener('click', this.useAlternate);

      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'hv-alt__dismiss button-unstyled';
      dismiss.dataset.hvCode = alt.code;
      dismiss.setAttribute('aria-label', `${box.dataset.hvDismiss || 'Forget'} ${alt.code}`);
      dismiss.textContent = '×';
      dismiss.addEventListener('click', this.dismissAlternate);

      item.append(code, delta, use, dismiss);
      list.append(item);
    }

    box.replaceChildren(lead, list);
    box.hidden = false;
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'request'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorRequest } = this.refs;
    this.#clearDiscountErrors();
    const target = type === 'request'
      ? cartDiscountErrorRequest
      : type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    if (!target) return;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
